/**
 * @name GxInfoWindow for Google Maps API V3. Originally called InfoBubble
 * @version 0.8
 * @author (original) Luke Mahe
 * Adapted by JS for GX.
 */

/*
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

// @ts-ignore
import {} from 'googlemaps';


/**
 * A CSS3 GxInfoWindow v1.0
 * @param {Object.<string, *>=} opt_options Optional properties to set.
 * @extends {google.maps.OverlayView}
 * @constructor
 */
export function GxInfoWindow(opt_options: Partial<{
  map: google.maps.Map;

  content: string;
  backgroundColor: string;
  color: string;

  position: google.maps.LatLng | google.maps.LatLngLiteral;
  pixelOffset: google.maps.Size;

  minWidth: number;
  minHeight: number;
  maxHeight: number;

  padding: number;

  borderRadius: number;
  borderWidth: number;
  borderColor: string;

  // for adjusting the map to move the info window into view
  disableAutoPan: boolean;
}>) {
  this.extend(GxInfoWindow, google.maps.OverlayView);
  this.baseZIndex_ = 100;
  this.isOpen_ = false;

  const options = opt_options || {};

  if (options['color'] == undefined) {
    options['color'] = this.COLOR_;
  }

  if (options['backgroundColor'] == undefined) {
    options['backgroundColor'] = this.BACKGROUND_COLOR_;
  }

  if (options['borderColor'] == undefined) {
    options['borderColor'] = this.BORDER_COLOR_;
  }

  if (options['borderRadius'] == undefined) {
    options['borderRadius'] = this.BORDER_RADIUS_;
  }

  if (options['borderWidth'] == undefined) {
    options['borderWidth'] = this.BORDER_WIDTH_;
  }

  if (options['padding'] == undefined) {
    options['padding'] = this.PADDING_;
  }

  if (options['disableAutoPan'] == undefined) {
    options['disableAutoPan'] = false;
  }

  if (options['disableAnimation'] == undefined) {
    options['disableAnimation'] = false;
  }

  if (options['minWidth'] == undefined) {
    options['minWidth'] = this.MIN_WIDTH_;
  }

  this.map_ = opt_options['map'];

  this.buildDom_();
  this.setValues(options);
}

/**
 * Default min width
 * @const
 * @private
 */
GxInfoWindow.prototype.MIN_WIDTH_ = 330;

/**
 * Default padding
 * @const
 * @private
 */
GxInfoWindow.prototype.PADDING_ = 10;


/**
 * Default border width
 * @const
 * @private
 */
GxInfoWindow.prototype.BORDER_WIDTH_ = 1;


/**
 * Default border color
 * @const
 * @private
 */
GxInfoWindow.prototype.BORDER_COLOR_ = 'transparent';


/**
 * Default border radius
 * @const
 * @private
 */
GxInfoWindow.prototype.BORDER_RADIUS_ = 4;


/**
 * Default background color
 * @const
 * @private
 */
GxInfoWindow.prototype.BACKGROUND_COLOR_ = '#fff';

/**
 * Default color
 * @const
 * @private
 */
GxInfoWindow.prototype.COLOR_ = '#252629';


/**
 * Extends an object's prototype.
 *
 * @param {Object} obj1 The object to be extended.
 * @param {Object} obj2 The object to extend with.
 * @return {Object} The new extended object.
 * @ignore
 */
GxInfoWindow.prototype.extend = function (obj1, obj2) {
  return (function (object) {
    for (const property in object.prototype) {
      this.prototype[property] = object.prototype[property];
    }
    return this;
  }).apply(obj1, [obj2]);
};


/**
 * Builds the GxInfoWindow dom
 * @private
 */
GxInfoWindow.prototype.buildDom_ = function () {
  const bubble = this.bubble_ = document.createElement('DIV');
  bubble.style['position'] = 'absolute';
  bubble.style['zIndex'] = this.baseZIndex_;
  bubble.style['overflowY'] = 'hidden';

  // Content area
  const contentContainer = this.contentContainer_ = document.createElement('DIV');
  contentContainer.style['overflowX'] = 'hidden';
  contentContainer.style['overflowY'] = 'auto';
  contentContainer.style['borderWidth'] = 'none';
  contentContainer.style['cursor'] = 'default';
  contentContainer.style['clear'] = 'both';
  contentContainer.style['position'] = 'relative';

  contentContainer.className = 'flex center text-truncate'; // display:flex & align-items:center

  const content = this.content_ = document.createElement('DIV');
  contentContainer.className = 'block overflow-hidden';
  contentContainer.appendChild(content);

  // Hide the GxInfoWindow by default
  bubble.style['display'] = 'none';

  bubble.appendChild(contentContainer);
};


/**
 * Sets the background class name
 *
 * @param {string} className The class name to set.
 */
GxInfoWindow.prototype.setBackgroundClassName = function (className) {
  this.set('backgroundClassName', className);
};
GxInfoWindow.prototype['setBackgroundClassName'] = GxInfoWindow.prototype.setBackgroundClassName;


/**
 * changed MVC callback
 */
GxInfoWindow.prototype.backgroundClassName_changed = function () {
  this.content_.className = this.get('backgroundClassName');
};
GxInfoWindow.prototype['backgroundClassName_changed'] = GxInfoWindow.prototype.backgroundClassName_changed;


/**
 * Set the zIndex of the GxInfoWindow
 *
 * @param {number} zIndex The zIndex to set.
 */
GxInfoWindow.prototype.setZIndex = function (zIndex) {
  this.set('zIndex', zIndex);
};
GxInfoWindow.prototype['setZIndex'] = GxInfoWindow.prototype.setZIndex;


/**
 * Get the zIndex of the GxInfoWindow
 *
 * @return {number} The zIndex to set.
 */
GxInfoWindow.prototype.getZIndex = function () {
  return parseInt(this.get('zIndex'), 10) || this.baseZIndex_;
};


/**
 * zIndex changed MVC callback
 */
GxInfoWindow.prototype.zIndex_changed = function () {
  const zIndex = this.getZIndex();

  this.bubble_.style['zIndex'] = this.baseZIndex_ = zIndex;
};
GxInfoWindow.prototype['zIndex_changed'] = GxInfoWindow.prototype.zIndex_changed;


/**
 * Set the background color
 *
 * @param {string} color The color to set.
 */
GxInfoWindow.prototype.setBackgroundColor = function (color) {
  if (color) {
    this.set('backgroundColor', color);
  }
};
GxInfoWindow.prototype['setBackgroundColor'] = GxInfoWindow.prototype.setBackgroundColor;


/**
 * backgroundColor changed MVC callback
 */
GxInfoWindow.prototype.backgroundColor_changed = function () {
  const backgroundColor = this.get('backgroundColor');
  this.contentContainer_.style['backgroundColor'] = backgroundColor;
};
GxInfoWindow.prototype['backgroundColor_changed'] = GxInfoWindow.prototype.backgroundColor_changed;


/**
 * Set the color
 *
 * @param {string} color The color to set.
 */
GxInfoWindow.prototype.setColor = function (color) {
  if (color) {
    this.set('color', color);
  }
};
GxInfoWindow.prototype['setColor'] = GxInfoWindow.prototype.setColor;


/**
 * color changed MVC callback
 */
GxInfoWindow.prototype.color_changed = function () {
  const color = this.get('color');
  this.contentContainer_.style['color'] = color;
};
GxInfoWindow.prototype['color_changed'] = GxInfoWindow.prototype.color_changed;


/**
 * Set the border color
 *
 * @param {string} color The border color.
 */
GxInfoWindow.prototype.setBorderColor = function (color) {
  if (color) {
    this.set('borderColor', color);
  }
};
GxInfoWindow.prototype['setBorderColor'] = GxInfoWindow.prototype.setBorderColor;


/**
 * borderColor changed MVC callback
 */
GxInfoWindow.prototype.borderColor_changed = function () {
  const borderColor = this.get('borderColor');

  const contentContainer = this.contentContainer_;
  contentContainer.style['borderColor'] = borderColor;


  contentContainer.style['borderStyle'] = 'solid';
};
GxInfoWindow.prototype['borderColor_changed'] = GxInfoWindow.prototype.borderColor_changed;


/**
 * Set the radius of the border
 *
 * @param {number} radius The radius of the border.
 */
GxInfoWindow.prototype.setBorderRadius = function (radius) {
  this.set('borderRadius', radius);
};
GxInfoWindow.prototype['setBorderRadius'] = GxInfoWindow.prototype.setBorderRadius;


/**
 * Get the radius of the border
 *
 * @private
 * @return {number} The radius of the border.
 */
GxInfoWindow.prototype.getBorderRadius_ = function () {
  return parseInt(this.get('borderRadius'), 10) || 0;
};


/**
 * borderRadius changed MVC callback
 */
GxInfoWindow.prototype.borderRadius_changed = function () {
  const borderRadius = this.getBorderRadius_();
  const borderWidth = this.getBorderWidth_();

  this.contentContainer_.style['borderRadius'] =
      this.contentContainer_.style['MozBorderRadius'] =
          this.contentContainer_.style['webkitBorderRadius'] = this.px(borderRadius);

  this.redraw_();
};
GxInfoWindow.prototype['borderRadius_changed'] = GxInfoWindow.prototype.borderRadius_changed;


/**
 * Get the width of the border
 *
 * @private
 * @return {number} width The width of the border.
 */
GxInfoWindow.prototype.getBorderWidth_ = function () {
  return parseInt(this.get('borderWidth'), 10) || 0;
};


/**
 * Set the width of the border
 *
 * @param {number} width The width of the border.
 */
GxInfoWindow.prototype.setBorderWidth = function (width) {
  this.set('borderWidth', width);
};
GxInfoWindow.prototype['setBorderWidth'] = GxInfoWindow.prototype.setBorderWidth;


/**
 * borderWidth change MVC callback
 */
GxInfoWindow.prototype.borderWidth_changed = function () {
  const borderWidth = this.getBorderWidth_();

  this.contentContainer_.style['borderWidth'] = this.px(borderWidth);

  this.borderRadius_changed();
  this.redraw_();
};
GxInfoWindow.prototype['borderWidth_changed'] = GxInfoWindow.prototype.borderWidth_changed;


/**
 * Set the padding of the GxInfoWindow
 *
 * @param {number} padding The padding to apply.
 */
GxInfoWindow.prototype.setPadding = function (padding) {
  this.set('padding', padding);
};
GxInfoWindow.prototype['setPadding'] = GxInfoWindow.prototype.setPadding;


/**
 * Set the padding of the GxInfoWindow
 *
 * @private
 * @return {number} padding The padding to apply.
 */
GxInfoWindow.prototype.getPadding_ = function () {
  return parseInt(this.get('padding'), 10) || 0;
};


/**
 * padding changed MVC callback
 */
GxInfoWindow.prototype.padding_changed = function () {
  const padding = this.getPadding_();
  this.contentContainer_.style['padding'] = this.px(padding);

  this.redraw_();
};
GxInfoWindow.prototype['padding_changed'] = GxInfoWindow.prototype.padding_changed;


/**
 * Add px extention to the number
 *
 * @param {number} num The number to wrap.
 * @return {string|number} A wrapped number.
 */
GxInfoWindow.prototype.px = function (num) {
  if (num) {
    // 0 doesn't need to be wrapped
    return num + 'px';
  }
  return num;
};


/**
 * Add events to stop propagation
 * @private
 */
GxInfoWindow.prototype.addEvents_ = function () {
  // We want to cancel all the events so they do not go to the map
  const events = ['mousedown', 'mousemove', 'mouseover', 'mouseout', 'mouseup',
    'mousewheel', 'click', 'DOMMouseScroll', 'touchstart', 'touchend', 'touchmove',
    'dblclick', 'contextmenu'];

  const bubble = this.bubble_;
  for (let i = 0; i < events.length; i++) {
    const event = events[i];
    bubble.addEventListener(event, function (e) {
      e.cancelBubble = true;
      if (e.stopPropagation) {
        e.stopPropagation();
      }
    });
  }
};


/**
 * On Adding the GxInfoWindow to a map
 * Implementing the OverlayView interface
 */
GxInfoWindow.prototype.onAdd = function () {
  if (!this.bubble_) {
    this.buildDom_();
  }

  this.addEvents_();

  const panes = this.getPanes();
  if (panes) {
    panes.floatPane.appendChild(this.bubble_);
  }

  /* once the infoBubble has been added to the DOM, fire 'domready' event */
  google.maps.event.trigger(this, 'domready');
};
GxInfoWindow.prototype['onAdd'] = GxInfoWindow.prototype.onAdd;


/**
 * Draw the GxInfoWindow
 * Implementing the OverlayView interface
 */
GxInfoWindow.prototype.draw = function () {
  const projection = this.getProjection();

  if (!projection) {
    // The map projection is not ready yet so do nothing
    return;
  }

  let latLng = /** @type {google.maps.LatLng} */ (this.getPosition());

  if (!latLng) {
    this.close();
    return;
  }

  const anchorHeight = this.getAnchorHeight_();

  const pos = projection.fromLatLngToDivPixel(latLng);
  let width = this.contentContainer_.offsetWidth;
  const height = this.bubble_.offsetHeight;

  if (!width) {
    return;
  }

  if (!this.pixelOffset
      || !this.pixelOffset.constructor
      || this.pixelOffset.constructor !== google.maps.Size) {
    this.pixelOffset = new google.maps.Size(0, 0);
  }

  // Adjust for the height of the info bubble
  let top = pos.y - (height) + this.pixelOffset.height;

  if (anchorHeight) {
    // If there is an anchor then include the height
    top -= anchorHeight;
  }

  const left = pos.x - (width) + this.pixelOffset.width;

  this.bubble_.style['top'] = this.px(top);
  this.bubble_.style['left'] = this.px(left);
};
GxInfoWindow.prototype['draw'] = GxInfoWindow.prototype.draw;


/**
 * Removing the GxInfoWindow from a map
 */
GxInfoWindow.prototype.onRemove = function () {
  if (this.bubble_ && this.bubble_.parentNode) {
    this.bubble_.parentNode.removeChild(this.bubble_);
  }
};
GxInfoWindow.prototype['onRemove'] = GxInfoWindow.prototype.onRemove;


/**
 * Is the GxInfoWindow open
 *
 * @return {boolean} If the GxInfoWindow is open.
 */
GxInfoWindow.prototype.isOpen = function () {
  return this.isOpen_;
};
GxInfoWindow.prototype['isOpen'] = GxInfoWindow.prototype.isOpen;


/**
 * Close the GxInfoWindow
 */
GxInfoWindow.prototype.close = function () {
  if (this.bubble_) {
    this.bubble_.style['display'] = 'none';
    // Remove the animation so we next time it opens it will animate again
    this.bubble_.className =
        this.bubble_.className.replace(this.animationName_, '');
  }

  this.isOpen_ = false;
};
GxInfoWindow.prototype['close'] = GxInfoWindow.prototype.close;


/**
 * Open the GxInfoWindow (asynchronous).
 *
 * @param {google.maps.Map=} opt_map Optional map to open on.
 * @param {google.maps.MVCObject=} opt_anchor Optional anchor to position at.
 */
GxInfoWindow.prototype.open = function (opt_map, opt_anchor) {
  const that = this;
  window.setTimeout(function () {
    that.open_(opt_map, opt_anchor);
  }, 0);
};


/**
 * Open the GxInfoWindow
 * @private
 * @param {google.maps.Map=} opt_map Optional map to open on.
 * @param {google.maps.MVCObject=} opt_anchor Optional anchor to position at.
 */
GxInfoWindow.prototype.open_ = function (opt_map, opt_anchor) {
  this.updateContent_();

  if (opt_map) {
    this.setMap(opt_map);
  }

  if (opt_anchor) {
    this.set('anchor', opt_anchor);
    this.bindTo('anchorPoint', opt_anchor);
    this.bindTo('position', opt_anchor);
  }

  // Show the bubble
  this.bubble_.style['display'] = '';
  const animation = !this.get('disableAnimation');

  if (animation) {
    // Add the animation
    this.bubble_.className += ' ' + this.animationName_;
  }

  this.redraw_();
  this.isOpen_ = true;

  const pan = !this.get('disableAutoPan');
  if (pan) {
    const that = this;
    window.setTimeout(function () {
      // Pan into view, done in a time out to make it feel nicer :)
      that.panToView();
    }, 200);
  }
};
GxInfoWindow.prototype['open'] = GxInfoWindow.prototype.open;


/**
 * Set the position of the GxInfoWindow
 *
 * @param {google.maps.LatLng} position The position to set.
 */
GxInfoWindow.prototype.setPosition = function (position) {
  if (position) {
    this.set('position', position);
  }
};
GxInfoWindow.prototype['setPosition'] = GxInfoWindow.prototype.setPosition;


/**
 * Returns the position of the GxInfoWindow
 *
 * @return {google.maps.LatLng} the position.
 */
GxInfoWindow.prototype.getPosition = function () {
  return /** @type {google.maps.LatLng} */ new google.maps.LatLng(this.get('position').lat, this.get('position').lng);
};
GxInfoWindow.prototype['getPosition'] = GxInfoWindow.prototype.getPosition;


/**
 * position changed MVC callback
 */
GxInfoWindow.prototype.position_changed = function () {
  this.draw();
};
GxInfoWindow.prototype['position_changed'] = GxInfoWindow.prototype.position_changed;

/**
 * Pan the GxInfoWindow into view
 */
GxInfoWindow.prototype.panToView = function () {
  // if we go beyond map, pan map
  const map = this.get('map');
  const projection = this.getProjection();
  const bounds = map.getBounds();
  if (!bounds || !projection) {
    return;
  }

  const offsetX_ = -(this.bubble_.offsetWidth / 2 - 20),
      offsetY_ = -(this.bubble_.offsetHeight + 20);

  const anchorPixel = projection.fromLatLngToDivPixel(this.getPosition());
  const top_left = new google.maps.Point(
          anchorPixel.x + offsetX_ - 30,
          anchorPixel.y + offsetY_ - 15,
      ),
      bottom_right = new google.maps.Point(
          top_left.x + this.bubble_.offsetWidth + 30,
          top_left.y + this.bubble_.offsetHeight + 15,
      );
  const sw = projection.fromDivPixelToLatLng(top_left),
      ne = projection.fromDivPixelToLatLng(bottom_right);


  // The bounds of the infowindow
  if (!map.getBounds().contains(ne) || !map.getBounds().contains(sw)) {
    map.panToBounds(new google.maps.LatLngBounds(sw, ne));
  }
};
GxInfoWindow.prototype['panToView'] = GxInfoWindow.prototype.panToView;


/**
 * Converts a HTML string to a document fragment.
 *
 * @param {string} htmlString The HTML string to convert.
 * @return {Node} A HTML document fragment.
 * @private
 */
GxInfoWindow.prototype.htmlToDocumentFragment_ = function (htmlString) {
  htmlString = htmlString.replace(/^\s*([\S\s]*)\b\s*$/, '$1');
  const tempDiv = document.createElement('DIV');
  tempDiv.innerHTML = htmlString;
  if (tempDiv.childNodes.length == 1) {
    return /** @type {!Node} */ (tempDiv.removeChild(tempDiv.firstChild));
  } else {
    const fragment = document.createDocumentFragment();
    while (tempDiv.firstChild) {
      fragment.appendChild(tempDiv.firstChild);
    }
    return fragment;
  }
};


/**
 * Removes all children from the node.
 *
 * @param {Node} node The node to remove all children from.
 * @private
 */
GxInfoWindow.prototype.removeChildren_ = function (node) {
  if (!node) {
    return;
  }

  let child;
  while (child = node.firstChild) {
    node.removeChild(child);
  }
};


/**
 * Sets the content of the infobubble.
 *
 * @param {string|Node} content The content to set.
 */
GxInfoWindow.prototype.setContent = function (content) {
  this.set('content', content);
};
GxInfoWindow.prototype['setContent'] = GxInfoWindow.prototype.setContent;


/**
 * Get the content of the infobubble.
 *
 * @return {string|Node} The marker content.
 */
GxInfoWindow.prototype.getContent = function () {
  return /** @type {Node|string} */ (this.get('content'));
};
GxInfoWindow.prototype['getContent'] = GxInfoWindow.prototype.getContent;


/**
 * Sets the marker content and adds loading events to images
 */
GxInfoWindow.prototype.updateContent_ = function () {
  if (!this.content_) {
    // The Content area doesnt exist.
    return;
  }

  this.removeChildren_(this.content_);
  let content = this.getContent();

  if (content) {
    if (typeof content == 'string') {
      content = this.htmlToDocumentFragment_(content);
    }
    this.content_.appendChild(content);

    const that = this;
    const images = this.content_.getElementsByTagName('IMG');
    for (let i = 0; i < images.length; i++) {
      // Because we don't know the size of an image till it loads, add a
      // listener to the image load so the marker can resize and reposition
      // itself to be the correct height.
      images[i].addEventListener('load', function () {
        that.imageLoaded_();
      });
    }
  }
  this.redraw_();
};


/**
 * Image loaded
 * @private
 */
GxInfoWindow.prototype.imageLoaded_ = function () {
  const pan = !this.get('disableAutoPan');
  this.redraw_();
  if (pan) {
    this.panToView();
  }
};

/**
 * Set the max width of the GxInfoWindow
 *
 * @param {number} width The max width.
 */
GxInfoWindow.prototype.setMaxWidth = function (width) {
  this.set('maxWidth', width);
};
GxInfoWindow.prototype['setMaxWidth'] = GxInfoWindow.prototype.setMaxWidth;


/**
 * maxWidth changed MVC callback
 */
GxInfoWindow.prototype.maxWidth_changed = function () {
  this.redraw_();
};
GxInfoWindow.prototype['maxWidth_changed'] = GxInfoWindow.prototype.maxWidth_changed;


/**
 * Set the max height of the GxInfoWindow
 *
 * @param {number} height The max height.
 */
GxInfoWindow.prototype.setMaxHeight = function (height) {
  this.set('maxHeight', height);
};
GxInfoWindow.prototype['setMaxHeight'] = GxInfoWindow.prototype.setMaxHeight;


/**
 * maxHeight changed MVC callback
 */
GxInfoWindow.prototype.maxHeight_changed = function () {
  this.redraw_();
};
GxInfoWindow.prototype['maxHeight_changed'] = GxInfoWindow.prototype.maxHeight_changed;


/**
 * Set the min width of the GxInfoWindow
 *
 * @param {number} width The min width.
 */
GxInfoWindow.prototype.setMinWidth = function (width) {
  this.set('minWidth', width);
};
GxInfoWindow.prototype['setMinWidth'] = GxInfoWindow.prototype.setMinWidth;


/**
 * minWidth changed MVC callback
 */
GxInfoWindow.prototype.minWidth_changed = function () {
  this.redraw_();
};
GxInfoWindow.prototype['minWidth_changed'] = GxInfoWindow.prototype.minWidth_changed;


/**
 * Set the min height of the GxInfoWindow
 *
 * @param {number} height The min height.
 */
GxInfoWindow.prototype.setMinHeight = function (height) {
  this.set('minHeight', height);
};
GxInfoWindow.prototype['setMinHeight'] = GxInfoWindow.prototype.setMinHeight;


/**
 * minHeight changed MVC callback
 */
GxInfoWindow.prototype.minHeight_changed = function () {
  this.redraw_();
};
GxInfoWindow.prototype['minHeight_changed'] = GxInfoWindow.prototype.minHeight_changed;

/**
 * Get the size of an element
 * @private
 * @param {Node|string} element The element to size.
 * @param {number=} opt_maxWidth Optional max width of the element.
 * @param {number=} opt_maxHeight Optional max height of the element.
 * @return {google.maps.Size} The size of the element.
 */
GxInfoWindow.prototype.getElementSize_ = function (element, opt_maxWidth,
                                                   opt_maxHeight) {
  // Don't bother to recalculate here based on the *true* element size.
  // We're setting the maxWith and maxHeight anyways so stick to those two.
  return new google.maps.Size(opt_maxWidth, opt_maxHeight);
};


/**
 * Redraw the GxInfoWindow
 * @private
 */
GxInfoWindow.prototype.redraw_ = function () {
  this.figureOutSize_();
  this.draw();
};


/**
 * Figure out the optimum size of the GxInfoWindow
 * @private
 */
GxInfoWindow.prototype.figureOutSize_ = function () {
  const map = this.get('map');

  if (!map) {
    return;
  }

  const mapDiv = map.getDiv();
  const gutter = 0;
  const mapWidth = mapDiv.offsetWidth - gutter;
  const mapHeight = mapDiv.offsetHeight - gutter - this.getAnchorHeight_();

  let width = /** @type {number} */ (this.get('minWidth') || 0);
  let height = /** @type {number} */ (this.get('minHeight') || 0);
  let maxWidth = /** @type {number} */ (this.get('maxWidth') || 0);
  let maxHeight = /** @type {number} */ (this.get('maxHeight') || 0);

  maxWidth = Math.min(mapWidth, maxWidth);
  maxHeight = Math.min(mapHeight, maxHeight);


  let content = /** @type {string|Node} */ (this.get('content'));
  if (typeof content == 'string') {
    content = this.htmlToDocumentFragment_(content);
  }
  if (content) {
    const contentSize = this.getElementSize_(content, maxWidth, maxHeight);

    if (width < contentSize.width) {
      width = contentSize.width;
    }

    // FORCE OVERRIDE DUE TO POSSIBLE Y-OVERFLOW WITHIN THE INFOBUBBLE
    height = this.get('maxHeight');
  }


  if (maxWidth) {
    width = Math.min(width, maxWidth);
  }

  if (maxHeight) {
    height = Math.min(height, maxHeight);
  }

  // Maybe add this as a option so they can go bigger than the map if the user
  // wants
  if (width > mapWidth) {
    width = mapWidth;
  }

  if (height > mapHeight) {
    height = mapHeight;
  }

  this.contentContainer_.style['width'] = this.px(width);
  this.contentContainer_.style['height'] = this.px(height);
  this.contentContainer_.style['max-height'] = this.px(maxHeight);
};


/**
 *  Get the height of the anchor
 *
 *  This function is a hack for now and doesn't really work that good, need to
 *  wait for pixelBounds to be correctly exposed.
 *  @private
 *  @return {number} The height of the anchor.
 */
GxInfoWindow.prototype.getAnchorHeight_ = function () {
  const anchor = this.get('anchor');
  if (anchor) {
    const anchorPoint = /** @type google.maps.Point */(this.get('anchorPoint'));

    if (anchorPoint) {
      return -1 * anchorPoint.y;
    }
  }
  return 0;
};

GxInfoWindow.prototype.anchorPoint_changed = function () {
  this.draw();
};
GxInfoWindow.prototype['anchorPoint_changed'] = GxInfoWindow.prototype.anchorPoint_changed;

